import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowLeft, ChevronRight, ExternalLink, Mail, MapPin } from 'lucide-react';
import { BrandLink } from '../components/BrandLogo';
import Seo from '../components/Seo';
import WebsiteFooter from '../components/WebsiteFooter';

// ── Legal document data ────────────────────────────────────────────────────────
const DOCS = {
  terms: {
    title: 'Terms of Service',
    subtitle: 'Please read these terms carefully before using WaapLeads.',
    updated: 'March 1, 2026',
    effective: 'March 1, 2026',
    sections: [
      {
        id: 'acceptance',
        heading: '1. Acceptance of Terms',
        content: [
          'By accessing or using the WaapLeads platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, do not access or use the Service.',
          'These Terms constitute a legally binding agreement between you ("User," "Customer," or "you") and WaapLeads Technologies Private Limited ("WaapLeads," "we," "us," or "our"), a company incorporated under the laws of India.',
          'We may update these Terms at any time. Continued use of the Service after any changes constitutes your acceptance of the new Terms. We will notify you of material changes via email or in-app notification at least 14 days before they take effect.',
        ],
      },
      {
        id: 'description',
        heading: '2. Description of Service',
        content: [
          'WaapLeads provides a multi-tenant Software-as-a-Service (SaaS) platform that enables businesses to communicate with their customers through WhatsApp via an API integration. Features include:',
          [
            'Multi-device WhatsApp number management',
            'Bulk and individual message sending',
            'Automated reply (chatbot) configuration',
            'Contact and phonebook management',
            'Campaign analytics and delivery reporting',
            'REST API access for programmatic integration',
            'Team and sub-account management',
          ],
          'WaapLeads is an independent platform and is not affiliated with, endorsed by, or sponsored by WhatsApp LLC or Meta Platforms, Inc. You are solely responsible for ensuring your use complies with WhatsApp\'s Terms of Service and Business Policy.',
        ],
      },
      {
        id: 'accounts',
        heading: '3. Account Registration & Security',
        content: [
          'To use WaapLeads, you must register for an account by providing accurate, current, and complete information. You agree to keep your account information updated at all times.',
          'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at support@waapleads.com of any unauthorized use of your account.',
          'We reserve the right to terminate accounts that provide false, inaccurate, or misleading information, or accounts associated with fraudulent or abusive activity.',
          'One organisation may maintain multiple sub-accounts (devices), but each registered company account must belong to a distinct legal entity or individual.',
        ],
      },
      {
        id: 'billing',
        heading: '4. Subscription & Billing',
        content: [
          'WaapLeads offers subscription plans billed on a monthly or annual basis. All prices are listed in Indian Rupees (₹) inclusive of applicable taxes unless stated otherwise.',
          'Payment is due at the start of each billing cycle. We use Razorpay as our payment processor. By subscribing, you authorise us to charge your chosen payment method on a recurring basis.',
          'Subscriptions auto-renew unless cancelled at least 24 hours before the renewal date. You may cancel your subscription at any time from your account settings; cancellation takes effect at the end of the current billing period.',
          'We do not offer refunds for partial billing periods. In exceptional circumstances (e.g., extended service outage exceeding 24 hours) we may, at our sole discretion, offer pro-rata credits.',
          'We reserve the right to modify pricing with 30 days\' written notice. Continued use after the notice period constitutes acceptance of the new pricing.',
        ],
      },
      {
        id: 'acceptable-use',
        heading: '5. Acceptable Use Policy',
        content: [
          'You agree not to use WaapLeads to:',
          [
            'Send unsolicited bulk messages (spam) to individuals who have not opted in to receive your communications',
            'Transmit unlawful, harassing, defamatory, obscene, fraudulent, or harmful content',
            'Impersonate any person or entity, or misrepresent your affiliation',
            'Distribute malware, phishing links, or other malicious code',
            'Violate any applicable local, national, or international law or regulation',
            'Circumvent any rate-limiting, security, or authentication measures',
            'Resell or sublicense access to the platform without prior written consent',
            'Use the Service for political campaigning without applicable disclosures',
            'Send messages to minors (under 18) without verifiable parental consent',
          ],
          'Violation of this policy may result in immediate suspension or termination of your account without refund, and may be reported to appropriate law enforcement authorities.',
        ],
      },
      {
        id: 'whatsapp-compliance',
        heading: '6. WhatsApp Policy Compliance',
        content: [
          'As an operator of a WhatsApp-connected platform, you must independently comply with WhatsApp\'s Terms of Service, Business Policy, and Commerce Policy at all times. Key requirements include:',
          [
            'Obtaining explicit opt-in consent from recipients before messaging them',
            'Honouring opt-out requests promptly (within 24 hours)',
            'Not sending prohibited content categories as defined by Meta',
            'Maintaining message frequency within acceptable limits to avoid number bans',
            'Using WhatsApp only for legitimate business communications',
          ],
          'WaapLeads provides rate-limiting tools to help you comply, but ultimate compliance responsibility rests with you. Number bans or blocks imposed by WhatsApp due to policy violations are outside our control and are not grounds for refund.',
        ],
      },
      {
        id: 'ip',
        heading: '7. Intellectual Property',
        content: [
          'WaapLeads and its licensors own all intellectual property rights in the Service, including software, design, trademarks, trade names, logos, and documentation. These Terms do not transfer any ownership rights to you.',
          'You are granted a limited, non-exclusive, non-transferable, revocable licence to use the Service solely for your internal business purposes in accordance with these Terms.',
          'You retain all rights to content you upload or transmit through the Service ("Customer Data"). By using the Service, you grant WaapLeads a limited licence to process your Customer Data solely to provide and improve the Service.',
          'You must not copy, modify, reverse-engineer, or create derivative works of any part of the Service.',
        ],
      },
      {
        id: 'liability',
        heading: '8. Disclaimers & Limitation of Liability',
        content: [
          'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.',
          'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WAAPLEADS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.',
          'OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO WAAPLEADS IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE CLAIM.',
          'Some jurisdictions do not allow limitations on implied warranties or exclusion of certain damages; in such jurisdictions, our liability shall be limited to the greatest extent permitted by law.',
        ],
      },
      {
        id: 'termination',
        heading: '9. Termination',
        content: [
          'Either party may terminate these Terms at any time. You may terminate by cancelling your subscription and ceasing use of the Service. We may terminate or suspend your access immediately, without prior notice, for:',
          [
            'Material breach of these Terms',
            'Suspected fraudulent or illegal activity',
            'Extended non-payment',
            'Requests from law enforcement or government agencies',
            'Discontinuation of the Service (with 30 days\' notice where possible)',
          ],
          'Upon termination, your right to use the Service ceases immediately. We will retain your Customer Data for 30 days post-termination, after which it will be permanently deleted. You may request a data export within this window.',
        ],
      },
      {
        id: 'governing-law',
        heading: '10. Governing Law & Disputes',
        content: [
          'These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.',
          'Any dispute arising under these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, disputes shall be submitted to binding arbitration in accordance with the Arbitration and Conciliation Act, 1996, with the seat of arbitration in Mumbai, Maharashtra.',
          'Nothing in this clause prevents either party from seeking urgent injunctive relief from a court of competent jurisdiction.',
        ],
      },
      {
        id: 'contact-terms',
        heading: '11. Contact Us',
        content: [
          'For questions about these Terms, please contact us at:',
          'WaapLeads Technologies Private Limited\nEmail: info@waapcoders.in\nAddress: Mumbai, Maharashtra, India\nPhone: +91 7392080372',
        ],
      },
    ],
  },

  privacy: {
    title: 'Privacy Policy',
    subtitle: 'Your privacy matters to us. Here is how we collect, use, and protect your data.',
    updated: 'March 1, 2026',
    effective: 'March 1, 2026',
    sections: [
      {
        id: 'intro-privacy',
        heading: '1. Introduction',
        content: [
          'WaapLeads Technologies Private Limited ("WaapLeads", "we", "us") operates the WaapLeads platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services.',
          'By using WaapLeads, you consent to the data practices described in this Policy. If you do not agree, please discontinue use of the Service.',
        ],
      },
      {
        id: 'information-collect',
        heading: '2. Information We Collect',
        content: [
          'We collect the following categories of information:',
          [
            'Account Information: Name, email address, phone number, company name, billing address, and GST number provided during registration',
            'Payment Information: Payment method details processed securely by Razorpay. We do not store full card numbers',
            'Usage Data: Log files, IP addresses, browser type, pages visited, features used, timestamps, and session duration',
            'Device Data: WhatsApp device information, QR code sessions, and connection status',
            'Message Metadata: Recipient numbers, message status (sent/delivered/read/failed), timestamps, and campaign IDs. We do not read message content unless required for debugging with your consent',
            'Communications: Emails and support tickets you send us',
            'Cookies & Tracking: See our Cookie Policy for details',
          ],
        ],
      },
      {
        id: 'use-information',
        heading: '3. How We Use Your Information',
        content: [
          'We use collected information to:',
          [
            'Provide, operate, and improve the Service',
            'Process transactions and send billing-related communications',
            'Send important service updates, security alerts, and support messages',
            'Respond to your enquiries and provide customer support',
            'Detect, prevent, and investigate fraud, abuse, and security incidents',
            'Analyse usage patterns to improve platform performance and features',
            'Comply with legal obligations, including tax and regulatory requirements',
            'Send marketing communications (only with your explicit consent; you may opt out at any time)',
          ],
          'We do not sell, rent, or trade your personal information to third parties for their marketing purposes.',
        ],
      },
      {
        id: 'sharing',
        heading: '4. Information Sharing & Disclosure',
        content: [
          'We share your information only in the following circumstances:',
          [
            'Service Providers: Trusted third-party processors (Razorpay, AWS/cloud hosting, analytics tools) who assist in operating the Service under strict data processing agreements',
            'Legal Requirements: When required by law, court order, or government authority',
            'Business Transfers: In connection with a merger, acquisition, or sale of assets, with advance notice to you',
            'Safety: To protect the rights, property, or safety of WaapLeads, our users, or the public',
            'With Your Consent: For any other purpose with your explicit prior consent',
          ],
          'All third-party service providers are contractually obligated to use your data only as directed by us and in accordance with this Policy.',
        ],
      },
      {
        id: 'retention',
        heading: '5. Data Retention',
        content: [
          'We retain your personal data for as long as your account is active or as needed to provide Services. Specifically:',
          [
            'Account data: Retained for the duration of your subscription plus 90 days post-termination',
            'Message metadata: Retained for up to 12 months for delivery reporting and analytics',
            'Billing records: Retained for 7 years as required by Indian tax law (GST records)',
            'Support communications: Retained for 3 years',
            'Anonymised analytics data: May be retained indefinitely',
          ],
          'You may request deletion of your data at any time. See Section 6 for your rights.',
        ],
      },
      {
        id: 'security',
        heading: '6. Data Security',
        content: [
          'We implement industry-standard security measures to protect your data:',
          [
            'TLS 1.2+ encryption for all data in transit',
            'AES-256 encryption for sensitive data at rest',
            'Role-based access control (RBAC) limiting employee access to personal data',
            'Regular security audits and penetration testing',
            'Multi-factor authentication (MFA) available for all accounts',
            'Automated monitoring for suspicious activity and data breaches',
          ],
          'No method of transmission over the internet or electronic storage is 100% secure. While we use commercially reasonable measures, we cannot guarantee absolute security. In the event of a data breach affecting your rights, we will notify you within 72 hours of becoming aware of it.',
        ],
      },
      {
        id: 'your-rights',
        heading: '7. Your Rights',
        content: [
          'Subject to applicable law, you have the following rights regarding your personal data:',
          [
            'Access: Request a copy of the personal data we hold about you',
            'Rectification: Request correction of inaccurate or incomplete data',
            'Erasure: Request deletion of your personal data ("right to be forgotten")',
            'Portability: Receive your data in a structured, machine-readable format',
            'Restriction: Request that we limit processing of your data in certain circumstances',
            'Objection: Object to processing based on legitimate interests or for direct marketing',
            'Withdraw Consent: Withdraw previously given consent at any time without affecting prior processing',
          ],
          'To exercise these rights, email us at info@waapcoders.in. We will respond within 30 days. Identity verification may be required before we fulfil your request.',
        ],
      },
      {
        id: 'third-party',
        heading: '8. Third-Party Services',
        content: [
          'The Service may contain links to third-party websites or integrate with third-party tools (e.g., Razorpay, Google Analytics). This Privacy Policy does not apply to those services. We encourage you to review the privacy policies of any third-party services you interact with.',
          'Our third-party integrations include: Razorpay (payment processing), Amazon Web Services (cloud infrastructure), and standard analytics tools. All are governed by their own privacy policies and our data processing agreements with them.',
        ],
      },
      {
        id: 'children',
        heading: '9. Children\'s Privacy',
        content: [
          'The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately at info@waapcoders.in and we will promptly delete such information.',
        ],
      },
      {
        id: 'contact-privacy',
        heading: '10. Contact & DPO',
        content: [
          'For privacy-related queries, data access requests, or complaints, contact our Data Protection Officer:',
          'Email: info@waapcoders.in\nWaapLeads Technologies Private Limited\nMumbai, Maharashtra, India\n\nIf you are unsatisfied with our response, you may lodge a complaint with the relevant data protection authority in your jurisdiction.',
        ],
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    subtitle: 'This policy explains how WaapLeads uses cookies and similar tracking technologies.',
    updated: 'March 1, 2026',
    effective: 'March 1, 2026',
    sections: [
      {
        id: 'what-cookies',
        heading: '1. What Are Cookies?',
        content: [
          'Cookies are small text files placed on your device when you visit a website. They help websites remember your preferences, keep you logged in, and collect analytics data to improve performance.',
          'Similar technologies include web beacons (pixel tags), local storage, and session storage. This Policy covers all such technologies collectively referred to as "cookies."',
        ],
      },
      {
        id: 'types-cookies',
        heading: '2. Types of Cookies We Use',
        content: [
          'We use the following categories of cookies:',
          [
            'Strictly Necessary Cookies: Required for the Service to function. These include authentication tokens (JWT), session identifiers, and CSRF protection tokens. You cannot opt out of these without disabling core functionality',
            'Performance & Analytics Cookies: Help us understand how users interact with the platform (page views, feature usage, error tracking). We use anonymised data and aggregate reports',
            'Functionality Cookies: Remember your preferences such as language, timezone, theme, and dashboard layout settings',
            'Marketing Cookies: Only used on our public landing page to measure campaign effectiveness. Not used inside the authenticated dashboard',
          ],
        ],
      },
      {
        id: 'cookie-list',
        heading: '3. Specific Cookies in Use',
        content: [
          'Key cookies and their purposes:',
          [
            'token (Session) — Authentication JWT. Expires when you log out or after 7 days',
            'authState (localStorage) — Persisted Redux auth state for faster loading. Cleared on logout',
            '_ga, _gid (30 days) — Google Analytics visitor tracking on public pages only',
            'razorpay_session (Session) — Required for Razorpay payment flows',
            'csrfToken (Session) — Cross-site request forgery protection',
          ],
        ],
      },
      {
        id: 'managing-cookies',
        heading: '4. Managing & Disabling Cookies',
        content: [
          'You can control cookies through your browser settings:',
          [
            'Chrome: Settings → Privacy and security → Cookies and other site data',
            'Firefox: Options → Privacy & Security → Cookies and Site Data',
            'Safari: Preferences → Privacy → Manage Website Data',
            'Edge: Settings → Cookies and site permissions',
          ],
          'Note: Disabling strictly necessary cookies will prevent you from logging in and using the Service. Disabling analytics cookies will not affect functionality but limits our ability to improve the platform.',
          'For marketing cookies on our public website, you may use standard browser controls or opt-out tools provided by our analytics partners.',
        ],
      },
      {
        id: 'third-party-cookies',
        heading: '5. Third-Party Cookies',
        content: [
          'Some cookies are set by third-party services we use:',
          [
            'Google Analytics: Web traffic analysis on public pages',
            'Razorpay: Required for payment processing flows',
            'Cloudflare (if applicable): Security and performance',
          ],
          'These third parties have their own privacy policies governing cookie use. We do not control these cookies and recommend reviewing the respective third-party policies.',
        ],
      },
      {
        id: 'updates-cookies',
        heading: '6. Updates to This Policy',
        content: [
          'We may update this Cookie Policy periodically. The "Last Updated" date at the top reflects the most recent revision. Material changes will be communicated via email or in-app notice.',
          'For questions about our use of cookies, contact: info@waapcoders.in',
        ],
      },
    ],
  },

  gdpr: {
    title: 'GDPR Compliance',
    subtitle: 'Information for EEA residents about how WaapLeads processes personal data under the General Data Protection Regulation.',
    updated: 'March 1, 2026',
    effective: 'March 1, 2026',
    sections: [
      {
        id: 'data-controller',
        heading: '1. Data Controller',
        content: [
          'For users in the European Economic Area (EEA), WaapLeads Technologies Private Limited acts as the Data Controller for personal data collected through the Service.',
          'Contact our Data Protection Officer (DPO) at: info@waapcoders.in',
        ],
      },
      {
        id: 'legal-basis',
        heading: '2. Legal Basis for Processing',
        content: [
          'We process your personal data on the following legal bases under GDPR Article 6:',
          [
            'Performance of Contract (Art. 6(1)(b)): Processing necessary to provide the Service, manage your account, and process payments',
            'Legal Obligation (Art. 6(1)(c)): Processing required by applicable law (e.g., tax records, anti-money laundering)',
            'Legitimate Interests (Art. 6(1)(f)): Security monitoring, fraud prevention, product improvement, and direct marketing to existing customers (with opt-out)',
            'Consent (Art. 6(1)(a)): For marketing to prospective customers, optional analytics, and any other processing explicitly requiring consent',
          ],
          'Where we rely on consent, you have the right to withdraw it at any time without affecting the lawfulness of prior processing.',
        ],
      },
      {
        id: 'your-rights-gdpr',
        heading: '3. Your Rights Under GDPR',
        content: [
          'As a data subject under GDPR, you have the following rights (Articles 15–22):',
          [
            'Right of Access (Art. 15): Obtain confirmation of whether we process your data and receive a copy',
            'Right to Rectification (Art. 16): Request correction of inaccurate or incomplete data',
            'Right to Erasure (Art. 17): Request deletion of your data where no legal basis exists for continued processing',
            'Right to Restriction (Art. 18): Request limitation of processing in specified circumstances',
            'Right to Data Portability (Art. 20): Receive your data in a structured, commonly used, machine-readable format (JSON/CSV)',
            'Right to Object (Art. 21): Object to processing based on legitimate interests, including direct marketing',
            'Rights Related to Automated Decision-Making (Art. 22): Not be subject to solely automated decisions with significant effects',
          ],
          'To exercise any of these rights, submit a request to info@waapcoders.in. We will respond within 30 days (extendable to 3 months for complex requests, with notice). We may ask you to verify your identity before acting on your request.',
        ],
      },
      {
        id: 'data-transfers',
        heading: '4. International Data Transfers',
        content: [
          'WaapLeads is headquartered in India. If you access the Service from within the EEA, your data may be transferred to and processed in India or other countries outside the EEA that may not provide the same level of data protection.',
          'We implement the following safeguards for international transfers:',
          [
            'Standard Contractual Clauses (SCCs) as approved by the European Commission',
            'Adequacy assessments for recipient countries where applicable',
            'Contractual data processing agreements with all sub-processors',
          ],
          'A list of our sub-processors and the safeguards in place is available upon request.',
        ],
      },
      {
        id: 'retention-gdpr',
        heading: '5. Data Retention',
        content: [
          'We retain personal data only for as long as necessary for the purposes described in our Privacy Policy, or as required by applicable law. Under GDPR, we apply the principle of storage limitation:',
          [
            'Active account data: Retained throughout subscription duration',
            'Post-cancellation: Account data deleted within 90 days; billing records retained for 7 years (legal obligation)',
            'Marketing data: Retained until you withdraw consent or object',
            'Support tickets: Retained for 3 years from resolution',
          ],
        ],
      },
      {
        id: 'data-breach',
        heading: '6. Data Breach Notification',
        content: [
          'In the event of a personal data breach that is likely to result in a risk to the rights and freedoms of individuals, we will:',
          [
            'Notify the relevant supervisory authority within 72 hours of becoming aware (Art. 33 GDPR)',
            'Notify affected data subjects without undue delay where the breach is likely to result in a high risk (Art. 34 GDPR)',
            'Maintain internal records of all data breaches (Art. 33(5) GDPR)',
          ],
        ],
      },
      {
        id: 'exercise-rights',
        heading: '7. How to Exercise Your Rights',
        content: [
          'You have several ways to exercise your GDPR rights:',
          [
            'Email: Send a request to info@waapcoders.in with subject line "GDPR Rights Request"',
            'In-App: Navigate to Settings → Privacy → Data Export/Delete to self-serve common requests',
            'Post: WaapLeads Technologies Private Limited, Mumbai, Maharashtra, India',
          ],
          'We will acknowledge receipt within 5 business days and fulfil the request within 30 days. For complex or multiple requests, we may extend this by up to two additional months with prior notice.',
        ],
      },
      {
        id: 'supervisory',
        heading: '8. Supervisory Authority',
        content: [
          'If you are located in the EEA and believe we have not handled your personal data in accordance with GDPR, you have the right to lodge a complaint with your local supervisory authority.',
          'You may find your local authority at: https://edpb.europa.eu/about-edpb/about-edpb/members_en',
          'We would, however, appreciate the opportunity to address your concerns before you contact the supervisory authority. Please reach out to us first at info@waapcoders.in.',
        ],
      },
    ],
  },
};

const NAV_LINKS = [
  { path: '/terms', label: 'Terms of Service' },
  { path: '/privacy', label: 'Privacy Policy' },
  { path: '/cookies', label: 'Cookie Policy' },
  { path: '/gdpr', label: 'GDPR' },
];

// ── Component ────────────────────────────────────────────────────────────────
export default function LegalPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const page = pathname.replace('/', ''); // 'terms' | 'privacy' | 'cookies' | 'gdpr'
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  const doc = DOCS[page];

  useEffect(() => {
    if (!doc) { navigate('/', { replace: true }); }
    window.scrollTo(0, 0);
  }, [page, doc, navigate]);

  // Highlight active TOC entry on scroll
  useEffect(() => {
    const onScroll = () => {
      const sections = contentRef.current?.querySelectorAll('section[id]') || [];
      let current = '';
      sections.forEach(el => {
        if (el.getBoundingClientRect().top <= 140) current = el.id;
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [page]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!doc) return null;

  return (
    <div className="legal-page">
      <Seo
        title={`${doc.title} | WaapLeads CRM`}
        description={doc.subtitle}
        pathname={pathname}
      />

      {/* ── Top Nav ── */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <BrandLink to="/" size={32} />

          <nav className="legal-doc-tabs">
            {NAV_LINKS.map(({ path, label }) => (
              <Link key={path} to={path} className={`legal-tab${path === pathname ? ' active' : ''}`}>
                {label}
              </Link>
            ))}
          </nav>

          <Link to="/" className="legal-back-btn">
            <ArrowLeft size={15} /> Back to Home
          </Link>
        </div>
      </header>

      {/* ── Hero banner ── */}
      <div className="legal-hero">
        <div className="legal-hero-inner">
          <div className="legal-breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={13} />
            <span>Legal</span>
            <ChevronRight size={13} />
            <span>{doc.title}</span>
          </div>
          <h1 className="legal-title">{doc.title}</h1>
          <p className="legal-subtitle">{doc.subtitle}</p>
          <div className="legal-meta">
            <span className="legal-badge">Last Updated: {doc.updated}</span>
            <span className="legal-badge legal-badge--outline">Effective: {doc.effective}</span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="legal-body">

        {/* Sidebar TOC */}
        <aside className="legal-toc">
          <div className="legal-toc-sticky">
            <p className="legal-toc-heading">Contents</p>
            <ul>
              {doc.sections.map(s => (
                <li key={s.id}>
                  <button
                    className={`legal-toc-link${activeSection === s.id ? ' active' : ''}`}
                    onClick={() => scrollTo(s.id)}
                  >
                    {s.heading}
                  </button>
                </li>
              ))}
            </ul>

            <div className="legal-toc-divider" />
            <p className="legal-toc-heading">Other Policies</p>
            <ul>
              {NAV_LINKS.filter(n => n.path !== `/${page}`).map(({ path, label }) => (
                <li key={path}>
                  <Link to={path} className="legal-toc-link legal-toc-other">
                    {label} <ExternalLink size={11} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <main className="legal-content" ref={contentRef}>
          {doc.sections.map(s => (
            <section key={s.id} id={s.id} className="legal-section">
              <h2 className="legal-section-heading">{s.heading}</h2>
              {s.content.map((block, i) =>
                Array.isArray(block) ? (
                  <ul key={i} className="legal-list">
                    {block.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} className="legal-paragraph">{block}</p>
                )
              )}
            </section>
          ))}

          {/* Contact card at bottom */}
          <div className="legal-contact-card">
            <h3>Have Questions?</h3>
            <p>Our team is happy to clarify anything about our policies.</p>
            <div className="legal-contact-items">
              <a href="mailto:info@waapcoders.in" className="legal-contact-item">
                <Mail size={16} /> info@waapcoders.in
              </a>
              <a href="tel:+917392080372" className="legal-contact-item">
                <MapPin size={16} /> +91 7392080372
              </a>
              <span className="legal-contact-item">
                <MapPin size={16} /> Mumbai, Maharashtra, India
              </span>
            </div>
          </div>
        </main>
      </div>

      {/* ── Footer ── */}
      <WebsiteFooter />
    </div>
  );
}

